import React, { useState } from 'react';
import {
  Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Number of year chips shown before the list is truncated behind a "Show more" toggle.
const YEARS_PREVIEW_LIMIT = 20;

/**
 * Hierarchical date filter component for Year > Month > Day drill-down.
 *
 * Props:
 * - facets: Object containing years, months, and days arrays from API
 * - selectedYear: Currently selected year (string) or null
 * - selectedMonth: Currently selected month (string, e.g., "2024-06") or null
 * - selectedDay: Currently selected day (string, e.g., "2024-06-15") or null
 * - onYearClick: Callback when year is clicked - (year: string) => void
 * - onMonthClick: Callback when month is clicked - (month: string) => void
 * - onDayClick: Callback when day is clicked - (day: string) => void
 * - onClearAll: Callback to clear all date filters - () => void
 * - themeColor: Optional theme color for chips - must be a valid MUI color name
 *               ('primary', 'secondary', 'error', 'info', 'success', 'warning')
 *               or 'default'. Hex colors are NOT supported and will cause errors.
 */
export default function DateHierarchyFilter({
  facets = {},
  selectedYear,
  selectedMonth,
  selectedDay,
  onYearClick,
  onMonthClick,
  onDayClick,
  onClearAll,
  themeColor = 'primary'
}) {
  // Validate themeColor - only allow valid MUI color names
  const validColors = ['primary', 'secondary', 'error', 'info', 'success', 'warning', 'default'];
  const chipColor = validColors.includes(themeColor) ? themeColor : 'primary';
  const { years = [], months = [], days = [] } = facets;

  // Whether the full year list is expanded (past the YEARS_PREVIEW_LIMIT truncation).
  // Only relevant in the rare case a single decade itself contains >20 years.
  const [showAllYears, setShowAllYears] = useState(false);

  // Currently-drilled decade (e.g. 2020) when no specific year is selected yet.
  const [selectedDecade, setSelectedDecade] = useState(null);

  // Decade that a given year belongs to (e.g. 2024 -> 2020).
  const decadeOf = (year) => Math.floor(parseInt(year, 10) / 10) * 10;

  // Format month for display (e.g., "2024-06" -> "June 2024")
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  };

  // Get short month name (e.g., "2024-06" -> "Jun")
  const getShortMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-AU', { month: 'short' });
  };

  // Format day for display (e.g., "2024-06-15" -> "15 June 2024")
  const formatDay = (dayStr) => {
    if (!dayStr) return '';
    const date = new Date(dayStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Get short day (e.g., "2024-06-15" -> "15")
  const getShortDay = (dayStr) => {
    if (!dayStr) return '';
    return dayStr.split('-')[2];
  };

  // Sort years in descending order (most recent first)
  const sortedYears = [...years].sort((a, b) => {
    const yearA = typeof a === 'object' ? a.value : a;
    const yearB = typeof b === 'object' ? b.value : b;
    return parseInt(yearB) - parseInt(yearA);
  });

  // Sort months in ascending order
  const sortedMonths = [...months].sort((a, b) => {
    const monthA = typeof a === 'object' ? a.value : a;
    const monthB = typeof b === 'object' ? b.value : b;
    return monthA.localeCompare(monthB);
  });

  // Sort days in ascending order
  const sortedDays = [...days].sort((a, b) => {
    const dayA = typeof a === 'object' ? a.value : a;
    const dayB = typeof b === 'object' ? b.value : b;
    return dayA.localeCompare(dayB);
  });

  // Group years into decades (descending), aggregating counts. Decades are derived
  // purely from the years the API already returned - no extra request needed.
  const decadeMap = new Map();
  sortedYears.forEach((yearItem) => {
    const yearVal = typeof yearItem === 'object' ? yearItem.value : yearItem;
    const cnt = typeof yearItem === 'object' && yearItem.count ? yearItem.count : 0;
    const dec = decadeOf(yearVal);
    if (Number.isNaN(dec)) return;
    const existing = decadeMap.get(dec) || { decade: dec, count: 0, years: 0 };
    existing.count += cnt;
    existing.years += 1;
    decadeMap.set(dec, existing);
  });
  const sortedDecades = [...decadeMap.values()].sort((a, b) => b.decade - a.decade);

  // Use the decade level only when it actually helps (more than one decade present).
  // For short-span datasets we drill straight from years (avoids a pointless single
  // decade chip). When a year is already selected we keep its decade in context.
  const useDecadeLevel = sortedDecades.length > 1;
  const activeDecade = selectedYear ? decadeOf(selectedYear) : selectedDecade;

  // Years within the active decade (descending).
  const yearsInActiveDecade = sortedYears.filter((yearItem) => {
    const yearVal = typeof yearItem === 'object' ? yearItem.value : yearItem;
    return decadeOf(yearVal) === activeDecade;
  });

  // Clear all date filters AND the local decade drill-down.
  const handleClearAll = () => {
    setSelectedDecade(null);
    if (onClearAll) onClearAll();
  };

  // If no date facets, don't render
  if (sortedYears.length === 0 && sortedMonths.length === 0 && sortedDays.length === 0) {
    return null;
  }

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">Date</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {/* Breadcrumb Navigation */}
        {(selectedDecade || selectedYear || selectedMonth || selectedDay) && (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label="All Years"
              size="small"
              variant="outlined"
              onClick={handleClearAll}
              sx={{ cursor: 'pointer' }}
            />
            {useDecadeLevel && activeDecade != null && (
              <>
                <Typography variant="body2" sx={{ mx: 0.5 }}>/</Typography>
                <Chip
                  label={`${activeDecade}s`}
                  size="small"
                  color={selectedYear ? 'default' : chipColor}
                  variant={selectedYear ? 'outlined' : 'filled'}
                  onClick={() => {
                    // Step back up to the year-in-decade view: clear the year (and below).
                    setSelectedDecade(activeDecade);
                    if (selectedYear && onClearAll) onClearAll();
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              </>
            )}
            {selectedYear && (
              <>
                <Typography variant="body2" sx={{ mx: 0.5 }}>/</Typography>
                <Chip
                  label={selectedYear}
                  size="small"
                  color={selectedMonth ? 'default' : chipColor}
                  variant={selectedMonth ? 'outlined' : 'filled'}
                  onClick={() => onYearClick(selectedYear)}
                  sx={{ cursor: 'pointer' }}
                />
              </>
            )}
            {selectedMonth && (
              <>
                <Typography variant="body2" sx={{ mx: 0.5 }}>/</Typography>
                <Chip
                  label={getShortMonth(selectedMonth)}
                  size="small"
                  color={selectedDay ? 'default' : chipColor}
                  variant={selectedDay ? 'outlined' : 'filled'}
                  onClick={() => onMonthClick(selectedMonth)}
                  sx={{ cursor: 'pointer' }}
                />
              </>
            )}
            {selectedDay && (
              <>
                <Typography variant="body2" sx={{ mx: 0.5 }}>/</Typography>
                <Chip
                  label={getShortDay(selectedDay)}
                  size="small"
                  color={chipColor}
                  sx={{ cursor: 'default' }}
                />
              </>
            )}
          </Box>
        )}

        {/* Decade level: shown first when there is more than one decade and no
            decade/year is selected yet. Selecting a decade reveals its years. */}
        {!selectedYear && useDecadeLevel && activeDecade == null && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {sortedDecades.map(({ decade, count }) => (
              <Chip
                key={decade}
                label={count ? `${decade}s (${count})` : `${decade}s`}
                size="small"
                variant="outlined"
                onClick={() => setSelectedDecade(decade)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        )}

        {/* Year level: either the years within the active decade, or - when the
            decade level isn't in use (short-span datasets) - all years flat with
            the expand/collapse fallback. */}
        {!selectedYear && (!useDecadeLevel || activeDecade != null) && sortedYears.length > 0 && (() => {
          const yearList = useDecadeLevel ? yearsInActiveDecade : sortedYears;
          const visibleYears = showAllYears ? yearList : yearList.slice(0, YEARS_PREVIEW_LIMIT);
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {visibleYears.map((yearItem) => {
                const year = typeof yearItem === 'object' ? yearItem.value : yearItem;
                const count = typeof yearItem === 'object' ? yearItem.count : null;
                return (
                  <Chip
                    key={year}
                    label={count ? `${year} (${count})` : year}
                    size="small"
                    variant="outlined"
                    onClick={() => onYearClick(year)}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
              {yearList.length > YEARS_PREVIEW_LIMIT && (
                <Chip
                  label={showAllYears ? 'Show less' : `+${yearList.length - YEARS_PREVIEW_LIMIT} more`}
                  size="small"
                  variant="outlined"
                  color={chipColor}
                  onClick={() => setShowAllYears((prev) => !prev)}
                  sx={{ cursor: 'pointer', fontStyle: 'italic' }}
                />
              )}
            </Box>
          );
        })()}

        {/* Show months if year selected but no month */}
        {selectedYear && !selectedMonth && sortedMonths.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {sortedMonths.map((monthItem) => {
              const month = typeof monthItem === 'object' ? monthItem.value : monthItem;
              const count = typeof monthItem === 'object' ? monthItem.count : null;
              return (
                <Chip
                  key={month}
                  label={count ? `${getShortMonth(month)} (${count})` : getShortMonth(month)}
                  size="small"
                  variant="outlined"
                  onClick={() => onMonthClick(month)}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>
        )}

        {/* Show days if month selected */}
        {selectedMonth && sortedDays.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {sortedDays.map((dayItem) => {
              const day = typeof dayItem === 'object' ? dayItem.value : dayItem;
              const count = typeof dayItem === 'object' ? dayItem.count : null;
              const isSelected = selectedDay === day;
              return (
                <Chip
                  key={day}
                  label={count ? `${getShortDay(day)} (${count})` : getShortDay(day)}
                  size="small"
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? chipColor : 'default'}
                  onClick={() => onDayClick(day)}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>
        )}

        {/* Show message when drilling down but no data */}
        {selectedYear && !selectedMonth && sortedMonths.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No records found for {selectedYear}
          </Typography>
        )}
        {selectedMonth && sortedDays.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No records found for {formatMonth(selectedMonth)}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
